import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api, type Reader } from '@/api/client';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import type { ContextMenuItem } from '@/components/ui/ContextMenu/ContextMenu';
import { ChevronRightIcon } from '@/components/ui/Icons';
import { useToast } from '@/contexts/ToastContext';
import {
  cancelReadersTableListQueries,
  optimisticPatchReaderInTableCaches,
  patchReaderInTableCache,
  invalidateReaderCachesExceptTable,
  restoreReadersTableInfiniteQueries,
  snapshotReadersTableInfiniteQueries,
  type ReadersTableInfiniteSnapshot,
} from '@/lib/readersCatalogQueryCache';
import '@/components/ProjectsTable/StateCell.css';
import '@/components/ProjectsTable/EditableCell.css';
import {
  normalizeReaderCooperationState,
  READER_COOPERATION_ICON_CLASS,
  READER_COOPERATION_MENU_ORDER,
  type ReaderCooperationStateValue,
} from '@/utils/readerCooperationStates';

type MutationCtx = { snapshot: ReadersTableInfiniteSnapshot };

export interface ReaderCatalogCooperationStateCellProps {
  reader: Reader;
  disabled?: boolean;
}

export function ReaderCatalogCooperationStateCell({
  reader,
  disabled = false,
}: ReaderCatalogCooperationStateCellProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const effectiveState = normalizeReaderCooperationState(reader.cooperation_state);

  const mutation = useMutation<Reader, Error, ReaderCooperationStateValue, MutationCtx>({
    mutationFn: (cooperation_state) =>
      api.readers.update(reader.id, { cooperation_state }),
    onMutate: async (nextState) => {
      await cancelReadersTableListQueries(queryClient);
      const snapshot = snapshotReadersTableInfiniteQueries(queryClient);
      optimisticPatchReaderInTableCaches(queryClient, reader.id, { cooperation_state: nextState });
      return { snapshot };
    },
    onSuccess: async (updated) => {
      showSuccess('Сотрудничество сохранено');
      patchReaderInTableCache(queryClient, updated);
      await invalidateReaderCachesExceptTable(queryClient);
      setMenuOpen(false);
      setAnchorEl(null);
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.snapshot) {
        restoreReadersTableInfiniteQueries(queryClient, ctx.snapshot);
      }
      showError(err instanceof Error ? err.message : String(err));
    },
  });

  const handleButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || mutation.isPending) return;
      setAnchorEl(e.currentTarget);
      setMenuOpen((o) => !o);
    },
    [disabled, mutation.isPending]
  );

  const handleClose = useCallback(() => {
    setMenuOpen(false);
    setAnchorEl(null);
  }, []);

  const menuItems: ContextMenuItem[] = READER_COOPERATION_MENU_ORDER.map((state) => ({
    label: state,
    icon: (
      <i
        className={clsx(
          READER_COOPERATION_ICON_CLASS[state],
          'editable-cell__context-menu-fa readers-table-catalog__cooperation-state-menu-fa'
        )}
        aria-hidden
      />
    ),
    onClick: () => {
      if (state !== effectiveState) {
        mutation.mutate(state);
      }
    },
    disabled: mutation.isPending,
  }));

  const isStopList = effectiveState === 'В стоп-листе';

  return (
    <>
      <div className="projects-table__state-cell-inner readers-table-catalog__state-cell-inner">
        <button
          type="button"
          className={clsx(
            'projects-table__action-btn projects-table__state-btn readers-table-catalog__state-btn',
            isStopList && 'readers-table-catalog__state-btn_stoplist'
          )}
          onClick={handleButtonClick}
          disabled={disabled || mutation.isPending}
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-label={`Сотрудничество: ${effectiveState}`}
        >
          <i
            className={clsx(
              READER_COOPERATION_ICON_CLASS[effectiveState],
              'projects-table__state-btn-icon readers-table-catalog__cooperation-state-fa'
            )}
            aria-hidden
          />
          <span className="projects-table__state-btn-text">{effectiveState}</span>
          <ChevronRightIcon size={12} className="projects-table__state-btn-chevron" />
        </button>
      </div>
      <ContextMenu
        isOpen={menuOpen}
        onClose={handleClose}
        anchorPosition={null}
        anchorEl={anchorEl}
        alignY="center"
        anchorPriorityAxis="y"
        repositionOnScroll={false}
        items={menuItems}
      />
    </>
  );
}
